import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { customersService } from '../services/customersService';
import { Customer, UserRole } from '../types';
import { toast } from 'react-toastify';
import { RootState } from '../store/store';
import { CustomerLocationsModal } from '../components/CustomerLocationsModal';
import { CustomersDetailModal } from '../components/CustomersDetailModal';

const CustomersPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterActive, setFilterActive] = useState<string>('all');
  const [locationsModalCustomer, setLocationsModalCustomer] = useState<Customer | null>(null);

  // Detail modal state
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const canManageCustomers = user?.role === UserRole.ORG_ADMIN ||
                             user?.role === UserRole.PLATFORM_ADMIN;

  // STAFF users cannot access customers page at all
  const canAccessCustomers = user?.role === UserRole.MANAGER ||
                             user?.role === UserRole.ORG_ADMIN ||
                             user?.role === UserRole.PLATFORM_ADMIN;

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const isActiveFilter =
        filterActive === 'all'
          ? undefined
          : filterActive === 'active';

      const data = await customersService.getAll(isActiveFilter);
      setCustomers(data);
    } catch (error: any) {
      console.error('Failed to load customers:', error);
      toast.error(t('customers.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterActive]);

  const handleRowClick = (customer: Customer) => {
    setSelectedCustomer(customer);
    setIsDetailModalOpen(true);
  };

  const handleEdit = () => {
    if (selectedCustomer) {
      navigate(`/app/customers/${selectedCustomer.id}`);
    }
  };

  const handleLocationsClick = (e: React.MouseEvent, customer: Customer) => {
    e.stopPropagation();
    setLocationsModalCustomer(customer);
  };

  if (!canAccessCustomers) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{t('errors.accessDenied')}</h2>
          <p className="text-gray-600 dark:text-gray-400">{t('errors.noPermission')}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600 dark:text-gray-400">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            {t('customers.title')}
          </h1>
          {canManageCustomers && (
            <button
              onClick={() => navigate('/app/customers/new')}
              className="w-full sm:w-auto px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors min-h-[44px] font-medium"
            >
              + {t('customers.addCustomer')}
            </button>
          )}
        </div>

        <div className="flex gap-4 mb-4 sm:mb-6">
          <div className="flex-1 sm:flex-none">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('common.status')}
            </label>
            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              className="w-full sm:w-auto px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white min-h-[44px]"
            >
              <option value="all">{t('common.all')}</option>
              <option value="active">{t('common.active')}</option>
              <option value="inactive">{t('common.inactive')}</option>
            </select>
          </div>
        </div>
      </div>

      {customers.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <p className="text-gray-500 dark:text-gray-400 text-lg">
            {t('customers.noCustomers')}
          </p>
          <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">
            {t('customers.businessSourceHint')}
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {customers.map((customer) => (
              <li key={customer.id}>
                <div
                  onClick={() => handleRowClick(customer)}
                  className="px-4 py-4 sm:px-6 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="text-base sm:text-lg font-medium text-blue-600 dark:text-blue-400 hover:underline">
                          {customer.name}
                        </h3>
                        {!customer.isActive && (
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                            {t('common.inactive')}
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-2 text-sm text-gray-600 dark:text-gray-400">
                        {customer.phone && (
                          <div className="truncate">
                            <span className="font-medium">{t('customers.phone')}:</span>{' '}
                            {customer.phone}
                          </div>
                        )}
                        {customer.email && (
                          <div className="truncate">
                            <span className="font-medium">{t('customers.email')}:</span>{' '}
                            {customer.email}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/app/customers/${customer.id}/users`); }}
                        className="inline-flex items-center gap-2 px-4 py-2.5 text-sm bg-teal-500 text-white rounded-lg hover:bg-teal-600 active:bg-teal-700 transition-colors min-h-[44px] font-semibold shadow-sm"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                        {t('users.title')}
                      </button>
                      <button
                        onClick={(e) => handleLocationsClick(e, customer)}
                        className="inline-flex items-center gap-2 px-4 py-2.5 text-sm bg-purple-500 text-white rounded-lg hover:bg-purple-600 active:bg-purple-700 transition-colors min-h-[44px] font-semibold shadow-sm"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {t('customers.locations')}
                      </button>
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Detail Modal */}
      <CustomersDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        customer={selectedCustomer}
        onEdit={handleEdit}
        canManage={canManageCustomers}
      />

      {/* Locations Modal */}
      {locationsModalCustomer && (
        <CustomerLocationsModal
          isOpen={!!locationsModalCustomer}
          onClose={() => setLocationsModalCustomer(null)}
          customer={locationsModalCustomer}
        />
      )}
    </div>
  );
};

export default CustomersPage;
